import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DialogContent, DialogActions } from '@mui/material';
import { SplitButton, ThemeButton } from '@shared/ui/buttons';
import { MemberTeams, FormGroup } from '@shared/ui/forms/display';
import { ListData, Status } from '@shared/ui/parts';
import DashboardStat from '@shared/ui/dashboard/DashboardStat';
import { getUserRole, roles } from '@shared/static/roles';
import EditMemberResend from '@widgets/members/EditMemberResend';
import { TeamMemberMobileMenu } from '@widgets/teams/menu';
import { EmailBtn, TelegramBtn } from '@widgets/buttons';
import {
  useFreezeMemberDialog,
  useMoveMemberDialog,
  useUnfreezeMemberDialog,
  useEditMemberDialog,
} from '@utils/dialogs-hooks';
import { formatBalanceUi, FRACTION_DIGITS } from '@utils/balance-helpers';
import { formatDateTime } from '@utils/date-helpers';
import {
  getColorsByPercentage,
  donutChartOptions,
} from '@utils/charts-helpers';
import { getTotalActiveHoldCards } from '@utils/helpers';

export default function MemberInfoDialog({ data, setSubtitle, setTitle }) {
  const { t } = useTranslation();
  const { openEditMemberDialog } = useEditMemberDialog();
  const { openFreezeMemberDialog } = useFreezeMemberDialog();
  const { openUnfreezeMemberDialog } = useUnfreezeMemberDialog();
  const { openMoveMemberDialog } = useMoveMemberDialog();

  // useEffect(() => {
  //   if (data.group_role === roles.TEAM_MEMBER) {
  //     dispatch(fetchAllGroups());
  //   }
  // }, [data.group_role]);

  useEffect(() => {
    setTitle(data.name);
    setSubtitle(
      <div>
        <Status data={data} type="team" /> {t(getUserRole(data.group_role))}
      </div>,
    );
  }, [data, t]);

  const totalCards = getTotalActiveHoldCards(data);

  const activeCardsLimit = Number(data.active_cards_limit) || 0;

  const chartData = useMemo(() => {
    return {
      series: [totalCards, Math.max(0, activeCardsLimit - totalCards)],
      options: {
        ...donutChartOptions.options,
        colors: getColorsByPercentage(totalCards, activeCardsLimit),
      },
    };
  }, [totalCards, activeCardsLimit]);

  return (
    <>
      <DialogContent>
        {!data.is_activated && <EditMemberResend data={data} />}
        <DashboardStat
          title={t('Cards')}
          chartData={chartData}
          value={`${totalCards} <span> / ${activeCardsLimit}</span>`}
        />
        <ListData
          listData={[
            {
              label: t('name'),
              value: data.name,
            },
            {
              label: t('created_at'),
              value: formatDateTime(data.created_at),
            },
          ]}
        />
        <br />
        <ListData
          listData={[
            {
              label: t('daily_card_issue_limit'),
              value: data.daily_card_issue_limit,
            },
            {
              label: t('active_cards_limit'),
              value: data.active_cards_limit,
            },
            {
              label: t('transaction_limit'),
              value: formatBalanceUi(
                data.transaction_limit,
                'USD',
                FRACTION_DIGITS.INTEGER,
              ),
            },
            {
              label: t('balance_limit'),
              value: formatBalanceUi(
                data.balance_limit,
                'USD',
                FRACTION_DIGITS.INTEGER,
              ),
            },
          ]}
        />
        <br />
        <MemberTeams data={data} hideCreateBtn />
        <br />
        <FormGroup label={t('contacts')}>
          <div className="form">
            {data.email && <EmailBtn email={data.email} text={data.email} />}
            {data.telegram && (
              <TelegramBtn username={data.telegram} text={data.telegram} />
            )}
          </div>
        </FormGroup>
      </DialogContent>
      <DialogActions>
        <div className="btns-toolbar">
          <ThemeButton
            minWidth
            appearance="secondary"
            startIcon="edit"
            onClick={() => openEditMemberDialog(data)}
          >
            {t('edit')}
          </ThemeButton>
          {data.group_role === roles.TEAM_LEAD ? (
            data.is_frozen ? (
              <ThemeButton
                appearance="secondary"
                startIcon="unfreeze"
                onClick={() => openUnfreezeMemberDialog(data)}
              >
                {t('unfreeze')}
              </ThemeButton>
            ) : (
              <ThemeButton
                appearance="freeze"
                startIcon="freeze"
                onClick={() => openFreezeMemberDialog(data)}
              >
                {t('freeze')}
              </ThemeButton>
            )
          ) : (
            <SplitButton
              text={t('move')}
              icon="transfer"
              action={() => openMoveMemberDialog(data)}
              menuComponent={TeamMemberMobileMenu}
              menuProps={{ data }}
            />
          )}
        </div>
      </DialogActions>
    </>
  );
}
